import React, {
    createContext,
    useContext,
    useMemo,
    useState,
} from "react";
import Requester from "../helpers/Requester";
import debounce from "lodash.debounce";

export type Grid = {
    id: number;
    symbol: string;
    currentPrice: number;
    stepBuy: number;
    stepSell: number;
    gridSize: number;
    quantityPerStep: number;
    status: "running" | "completed" | "cancelled";
    realizedProfit: number;
    closedCycles: number;
    createdAt: string;
    orders: Array<{
        id: number;
        price: number;
        quantity: number;
        type: "BUY" | "SELL";
        status: "open" | "filled" | "cancelled";
        binanceOrderId: string;
        fee: number;
        feeAsset: string;
        isMaker: boolean;
        executedAt: string;
    }>;
};

interface BinanceState {
    balances: Record<string, any>;
    prices: Record<string, any>;
    priceDirections: Record<string, boolean>;
    openTrades: Array<any>;
    trades: Array<any>;
    grids: Grid[]
    setBalances: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    setPrices: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    setOpenTrades: React.Dispatch<React.SetStateAction<Array<any>>>;
    setTrades: React.Dispatch<React.SetStateAction<Array<any>>>;
    setPriceDirections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    setGrids: React.Dispatch<React.SetStateAction<Grid[]>>;
    updateBalances: () => void;
    updateOpenTrades: () => void;
    updateTrades: () => void;
}

const BinanceContext = createContext<BinanceState | null>(null);

function useBinanceState(): BinanceState {
    return useContext(BinanceContext) as unknown as BinanceState;
}

const requester = new Requester();

function BinanceProvider({ children }: { children: React.ReactNode }): JSX.Element {
    //@ts-ignore
    const [balances, setBalances] = useState<Record<string, any>>({});
    const [openTrades, setOpenTrades] = useState<Array<any>>([]);
    const [trades, setTrades] = useState<Array<any>>([]);
    const [prices, setPrices] = useState<Record<string, any>>({});
    const [grids, setGrids] = useState<Grid[]>([]);
    const [priceDirections, setPriceDirections] = useState<Record<string, boolean>>({});



    const updateBalances = useMemo(
        () =>
            debounce(() => {
                requester
                    .get(requester.getUrl("api/balances"))
                    .then((response) => setBalances(response))
                    .catch(() => { });
            }, 500),
        []
    );

    const updateOpenTrades = useMemo(
        () =>
            debounce(() => {
                requester
                    .get(requester.getUrl("api/open-trades"))
                    .then((response) => setOpenTrades(response))
                    .catch(() => { });
            }, 500),
        []
    );

    const updateTrades = useMemo(
        () =>
            debounce(() => {
                requester
                    .get(requester.getUrl("api/trades"))
                    .then((response) => setTrades(response))
                    .catch(() => { });
            }, 500),
        []
    );

    return (
        <BinanceContext.Provider value={{
            balances,
            setBalances,
            prices,
            setPrices,
            updateBalances,
            openTrades,
            setOpenTrades,
            updateOpenTrades,
            setPriceDirections,
            priceDirections,
            trades,
            setTrades,
            updateTrades,
            grids,
            setGrids
        }}>
            {children}
        </BinanceContext.Provider>
    );
}

export { BinanceProvider, useBinanceState };
